/**
 * Phase F1+F2+F3+F4+F9 — Local LLM Endpoints admin UI section.
 *
 * Mounted inside AdminView's "API Keys & Services" tab below the
 * existing API Keys card. Encapsulates:
 *
 *   F1 — list view: status pill + model count + default model + last
 *        health badge per endpoint
 *   F2 — Add Endpoint modal wizard (slug + display_name + provider class
 *        + base_url + api_key + default_model + test → save)
 *   F3 — per-row actions: Edit / Test / Disable / Delete (delete shows
 *        409 detail with reference list when blocked)
 *   F4 — provider class dropdown populated from
 *        GET /api/llm/local-provider-classes
 *   F9 — starter ranking template buttons surfaced after first endpoint
 *        save (one-click seeds tenant rankings with Local-Only or
 *        Local-with-Cloud-Fallback shape)
 *
 * BYOK-strict UI invariant: api_key is NEVER fetched back from the
 * server. Edit modal shows ``api_key_set`` as the mask; admins type a
 * new key only when rotating. The wizard's "Test Connection" button
 * uses the ephemeral /test-connection endpoint so probes don't persist.
 *
 * Design ref: ``Projects/agentic-platform/docs/design/llama-4-scout-integration-v1.md``
 * §14 Phase F.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { COLORS } from "../../theme/colors";
import {
  Badge,
  Button,
  IconButton,
  Input,
  Label,
  Modal,
  Select,
  TogglePill,
} from "../../components/ui";
import { localLlmApi, authApi } from "../../api/client";
import { createLogger } from "../../utils/logger";
import ModelServingLight from "./ModelServingLight";
import HostedEndpointCells from "./HostedEndpointCells";
import { EndpointLayout, EndpointTableHead, endpointLayout } from "./localLlmLayout";
import { useElementWidth } from "../../hooks/useElementWidth";

const log = createLogger("local-llm");

// Status → badge colour mapping. Mirrors the integrations table
// pattern in AdminView.
const HEALTH_COLOURS = {
  healthy: "#10B981",
  degraded: "#F59E0B",
  unreachable: "#EF4444",
  warming: "#F59E0B",
  parked: COLORS.muted,
  unknown: COLORS.muted,
};


// Exported for unit testing — tenant-scoped slug rule (lowercase alnum start,
// then alnum / dash / underscore, max 63 chars). Mirrors the backend validator.
export function isValidSlug(slug) {
  return /^[a-z0-9][a-z0-9_-]{0,62}$/.test(slug);
}

// Exported for unit testing — shape the chat ranking entries a starter
// template seeds. "local_only" = single local endpoint; anything else =
// local endpoint first, then an OpenAI cloud fallback.
export function buildStarterChatEntries(template, { endpointId, defaultModel }) {
  const local = {
    provider: "local_openai_compat",
    model: defaultModel || "llama-4-scout",
    endpoint_id: endpointId,
  };
  return template === "local_only"
    ? [local]
    : [local, { provider: "openai", model: "gpt-4o" }];
}


function HealthBadge({ status }) {
  const color = HEALTH_COLOURS[status] || COLORS.muted;
  const label = status || "untested";
  return (
    <Badge color={color} style={{ textTransform: "capitalize" }}>
      {label}
    </Badge>
  );
}


// Status cell — the Enabled/Disabled badge plus, for on-demand endpoints, the live
// Red/Amber/Green warm-up light. Written as single-line statements (not a multi-line
// JSX tree) so v8 attributes each line deterministically — nested multi-line JSX
// inside the table .map() attributes to the map root and flakes the diff-cov gate.
function EndpointStatusCell({ row }) {
  const badge = <Badge color={row.enabled ? "#10B981" : COLORS.muted}>{row.enabled ? "Enabled" : "Disabled"}</Badge>;
  const light = row.is_on_demand ? <ModelServingLight endpointId={row.endpoint_id} compact /> : null;
  return <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6 }}>{badge}{light}</div>;
}


// One endpoint, laid out for the width the list has (table / compact / cards). A
// platform-hosted model (managed in Model Serving) gets the shared live status,
// read-only; an endpoint you run yourself keeps the probe health and Test / Edit /
// Disable / Delete.
function EndpointRow({ row, layout, onTest, onEdit, onToggle, onDelete }) {
  if (row.managed_by === "model_serving") return <HostedEndpointCells row={row} layout={layout} onToggle={() => onToggle(row)} />;
  // single-line statements (see EndpointStatusCell): v8 attributes each line reliably
  const health = <HealthBadge status={row.last_health_status} />;
  const test = <IconButton icon="🔍" title="Test connection" onClick={() => onTest(row)} />;
  const edit = <IconButton icon="✏️" title="Edit" onClick={() => onEdit(row)} />;
  const toggle = <IconButton icon={row.enabled ? "🚫" : "✅"} title={row.enabled ? "Disable" : "Enable"} onClick={() => onToggle(row)} />;
  const remove = <IconButton icon="🗑️" title="Delete" onClick={() => onDelete(row)} />;
  const actions = <div style={{ display: "inline-flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 6 }}>{test}{edit}{toggle}{remove}</div>;
  return <EndpointLayout row={row} layout={layout} parts={{ health, status: <EndpointStatusCell row={row} />, actions }} />;
}


/**
 * H1 — "Llama 4 Scout starter rankings" affordance.
 *
 * Surfaced on the Rankings tab when:
 *   - the tenant has ≥1 registered local LLM endpoint, AND
 *   - the current chat ranking is empty OR the first entry isn't local.
 *
 * Single click seeds the chat ranking with the local endpoint and (optional)
 * cloud fallback. Same logic as the F9 wizard final step, but standalone —
 * admins who skipped the wizard's offer can come back later.
 */
export function StarterRankingsCard({ availableSlugs, currentRankings, onSeeded }) {
  const [seeding, setSeeding] = useState(null);
  const [error, setError] = useState(null);

  // Hooks MUST run unconditionally before any early return — compute the
  // primary slug defensively so the useCallback below can hoist above the
  // "no endpoints / already configured" guards (React error #310 otherwise).
  const primarySlug = availableSlugs?.[0];

  const seed = useCallback(async (template) => {
    setSeeding(template); setError(null);
    try {
      // Default model: read it via the live models proxy. That proxy now
      // 502s when the endpoint is unreachable (e.g. an on-demand model parked
      // at zero replicas), so fall back to the configured default on the
      // endpoint row before the "llama-4-scout" starter default.
      let defaultModel = null;
      try {
        const r = await localLlmApi.models(primarySlug);
        defaultModel = r?.data?.default_model || null;
      } catch (_) {
        try {
          const l = await localLlmApi.list();
          defaultModel = (l?.data || []).find((e) => e.endpoint_id === primarySlug)?.default_model || null;
        } catch (_e) { /* non-fatal; use the starter default */ }
      }
      defaultModel = defaultModel || "llama-4-scout";

      const chatEntries = buildStarterChatEntries(template, {
        endpointId: primarySlug,
        defaultModel,
      });
      await authApi.setAllowCloudFallback(template === "local_with_cloud_fallback");
      await authApi.updateLlmRankings("chat", chatEntries);
      if (onSeeded) onSeeded();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSeeding(null);
    }
  }, [primarySlug, onSeeded]);

  if (!availableSlugs || availableSlugs.length === 0) return null;
  const chatRanking = (currentRankings?.chat) || [];
  const firstIsLocal = chatRanking[0]?.provider?.startsWith("local_");
  if (firstIsLocal) return null; // already configured

  return (
    <div
      style={{
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        padding: "16px 20px",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>
        🚀 Quick start — Llama 4 Scout starter ranking
      </div>
      <div style={{ fontSize: 12, color: COLORS.muted, lineHeight: 1.5, marginBottom: 12 }}>
        You have <strong>{availableSlugs.length}</strong> local LLM endpoint
        {availableSlugs.length > 1 ? "s" : ""} registered (<code>{primarySlug}</code>
        {availableSlugs.length > 1 ? ", …" : ""}). Apply a starter ranking
        for <code>chat</code> in one click:
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Button
          variant="primary"
          onClick={() => seed("local_only")}
          disabled={seeding !== null}
        >
          {seeding === "local_only" ? "Seeding…" : "🔒 Local Only (Air-gapped)"}
        </Button>
        <Button
          variant="secondary"
          onClick={() => seed("local_with_cloud_fallback")}
          disabled={seeding !== null}
        >
          {seeding === "local_with_cloud_fallback" ? "Seeding…" : "☁️ Local + Cloud Fallback"}
        </Button>
      </div>
      {error && (
        <div style={{ fontSize: 11, color: "#EF4444", marginTop: 8 }}>
          {error}
        </div>
      )}
    </div>
  );
}


/**
 * F7 — "Verify all-local" sanity banner.
 *
 * Backed by ``GET /api/llm/non-local-purposes`` (Phase L L5) so the
 * purpose list is authoritative (enumerated from backend
 * ``VALID_PURPOSES``, not a hardcoded frontend list that can drift).
 *
 * Renders three states:
 *  - chat NOT local → no banner (the partial-local gap isn't relevant
 *    if chat itself is cloud)
 *  - chat IS local + zero cloud-routed purposes → no banner (fully
 *    local config)
 *  - chat IS local + some cloud-routed purposes:
 *    - allow_cloud_fallback=True  → amber warning (works at runtime
 *      via cloud fallthrough; surface as informational)
 *    - allow_cloud_fallback=False → red error (Q11 chokepoint will
 *      block these purposes at resolve time — the tenant's air-gap
 *      contract is incomplete; needs fixing before any embed/intent/
 *      etc. call lands)
 *
 * Pre-2026-05-25 (Phase L L5) this was a frontend-only check against
 * a hardcoded 8-purpose list. The L5 extension adds the backend
 * endpoint so any new purpose surfaces automatically + the severity
 * differentiates by fallback state.
 *
 * Props: optional ``rankings`` (legacy callsite) is ignored — the
 * banner now fetches its own data. Kept as a prop so existing call
 * sites don't break.
 */
export function VerifyAllLocalBanner({ rankings: _legacyRankings }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await localLlmApi.nonLocalPurposes();
        if (!cancelled) setState(res?.data || null);
      } catch (_err) {
        // Endpoint unavailable (older API, network issue) — fail silent;
        // the banner is informational, not a security gate.
        if (!cancelled) setState(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!state) return null;
  if (!state.chat_is_local) return null;

  const nonLocal = state.non_local_purposes || [];
  if (nonLocal.length === 0) return null;

  const fallbackOff = !state.allow_cloud_fallback;
  const purposeList = nonLocal.map(p => p.purpose).join(", ");

  // Severity-aware styling: red when Q11 will block these purposes,
  // amber when they'll still work via cloud fallthrough.
  const colors = fallbackOff
    ? {
        bg: "rgba(239, 68, 68, 0.10)",  // red-500 at 10%
        border: "rgba(239, 68, 68, 0.5)",
        icon: "⛔",
      }
    : {
        bg: "rgba(245, 158, 11, 0.08)", // amber-500 at 8%
        border: "rgba(245, 158, 11, 0.4)",
        icon: "⚠️",
      };

  return (
    <div
      style={{
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        padding: "12px 16px",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
      }}
    >
      <div style={{ fontSize: 18 }}>{colors.icon}</div>
      <div style={{ flex: 1, fontSize: 12, color: COLORS.text, lineHeight: 1.5 }}>
        {fallbackOff ? (
          <>
            <strong>Self-hosted setup incomplete — some features will not work.</strong>
            <br />
            Your chat traffic is set to a local LLM, and you've turned
            Cloud LLM Fallback OFF (so this tenant stays self-hosted). But
            these features are still pointed at cloud providers:{" "}
            <strong>{purposeList}</strong>. They will be refused until you
            either point each one at a local LLM endpoint, or turn Cloud LLM
            Fallback ON to allow cloud as a backup.
          </>
        ) : (
          <>
            <strong>Some features still use cloud providers.</strong>
            <br />
            Your chat traffic routes to a local LLM, but these features still
            go to the cloud: <strong>{purposeList}</strong>.
            <br />
            For a fully self-hosted setup, point each of these at a local LLM
            endpoint. If running them in the cloud is fine for you, you can
            ignore this notice.
          </>
        )}
      </div>
    </div>
  );
}


/**
 * F8 — Cloud LLM Fallback toggle card.
 *
 * Mounted at the top of the LLM Rankings tab. Reads + writes the
 * tenant ``allow_cloud_fallback`` field via the dedicated
 * /api/auth/allow-cloud-fallback endpoint. Hot — no app restart.
 */
export function CloudFallbackToggleCard() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await authApi.getAllowCloudFallback();
        setEnabled(!!res?.data?.allow_cloud_fallback);
      } catch (err) {
        setError(err.message || String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = useCallback(async () => {
    if (saving || loading) return;
    setSaving(true); setError(null);
    const newValue = !enabled;
    try {
      await authApi.setAllowCloudFallback(newValue);
      setEnabled(newValue);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  }, [enabled, saving, loading]);

  return (
    <div
      style={{
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        padding: "16px 20px",
        display: "flex",
        alignItems: "center",
        gap: 16,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
          🔒 Cloud LLM Fallback
        </div>
        <div style={{ fontSize: 12, color: COLORS.muted, lineHeight: 1.5 }}>
          {enabled
            ? "ON — if your local LLM endpoints are unreachable, the platform " +
              "falls back to the cloud providers configured below. Use this " +
              "when uptime matters more than keeping traffic self-hosted."
            : "OFF (default) — your data stays self-hosted. If your local LLM " +
              "endpoints are unreachable, requests fail with a clear error " +
              "instead of quietly going to a cloud provider. Recommended for " +
              "self-hosted and sovereign deployments."}
        </div>
        {error && (
          <div style={{ fontSize: 11, color: "#EF4444", marginTop: 6 }}>
            {error}
          </div>
        )}
      </div>
      <TogglePill
        active={enabled}
        onClick={toggle}
        disabled={loading || saving}
        title={enabled ? "Click to disable cloud fallback (self-hosted only)" : "Click to enable cloud fallback"}
      >
        {loading ? "..." : (enabled ? "ON — cloud backup enabled" : "OFF — self-hosted only")}
      </TogglePill>
    </div>
  );
}


export default function LocalLLMEndpointsSection({ onSeedRankings }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [providerClasses, setProviderClasses] = useState([]);

  // Modal state — null = closed; "create" | "edit" | { mode:"delete", row }
  const [modal, setModal] = useState(null);
  const [editRow, setEditRow] = useState(null);

  // the list lays itself out for the width it has, not the window (localLlmLayout)
  const listRef = useRef(null);
  const layout = endpointLayout(useElementWidth(listRef));

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [list, classes] = await Promise.all([
        localLlmApi.list(),
        localLlmApi.providerClasses(),
      ]);
      setRows(list?.data || []);
      setProviderClasses((classes?.data || []).filter(c => c.visible_in_admin));
      setError(null);
    } catch (err) {
      log.error("Failed to load local LLM endpoints:", err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleTest = useCallback(async (row) => {
    try {
      const res = await localLlmApi.test(row.endpoint_id);
      if (res?.data?.ok) {
        alert(
          `OK — ${res.data.models_count ?? 0} model(s) available\n` +
          `Latency: ${res.data.latency_ms ?? "?"} ms`
        );
      } else {
        alert(
          "Endpoint unreachable. " +
          (res?.data?.error || res?.error?.message || "(unknown error)")
        );
      }
    } catch (err) {
      alert(`Test failed: ${err.message || err}`);
    }
    await reload();
  }, [reload]);

  const handleDelete = useCallback(async (row) => {
    if (!confirm(`Delete endpoint ${row.endpoint_id}? This cannot be undone.`)) return;
    try {
      await localLlmApi.remove(row.endpoint_id);
      await reload();
    } catch (err) {
      // 409 carries structured detail with rankings + remediation
      const detail = err?.payload?.detail;
      if (detail && typeof detail === "object" && detail.error === "endpoint_referenced") {
        const refs = (detail.rankings || []).map(r =>
          `  • ${r.purpose}${r.tier ? `[${r.tier}]` : ""} position ${r.position}`
        ).join("\n");
        alert(
          `Cannot delete ${row.endpoint_id} — still referenced in:\n\n${refs}\n\n` +
          (detail.remediation || "Remove references first.")
        );
      } else {
        alert(`Delete failed: ${err.message || err}`);
      }
    }
  }, [reload]);

  const handleToggleEnabled = useCallback(async (row) => {
    try {
      await localLlmApi.update(row.endpoint_id, {
        enabled: !row.enabled,
      });
      await reload();
    } catch (err) {
      alert(`Toggle failed: ${err.message || err}`);
    }
  }, [reload]);


  const rowHandlers = {
    onTest: handleTest, onToggle: handleToggleEnabled, onDelete: handleDelete,
    onEdit: (row) => { setEditRow(row); setModal("edit"); },
  };
  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
          Local LLM Endpoints
        </div>
        <Badge color={COLORS.accent}>{rows.length}</Badge>
        <div style={{ flex: 1 }} />
        <Button variant="secondary" onClick={() => setModal("create")}>
          + Add Endpoint
        </Button>
      </div>

      <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 16, lineHeight: 1.5 }}>
        Register an OpenAI-compatible local LLM server (Ollama, vLLM,
        LM Studio, TGI, NIM). Each endpoint is addressable by its
        slug in LLM Rankings. <strong>BYOK invariant:</strong> credentials
        never leave the database; the UI only shows whether a key is stored.
      </div>

      {error && (
        <div
          style={{
            background: "rgba(239, 68, 68, 0.08)",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            borderRadius: 8,
            padding: 12,
            fontSize: 13,
            color: COLORS.text,
            marginBottom: 12,
          }}
        >
          Failed to load endpoints: {error}
        </div>
      )}

      {!loading && rows.length === 0 && !error && (
        <div
          style={{
            background: COLORS.bgElevated,
            border: `1px dashed ${COLORS.border}`,
            borderRadius: 8,
            padding: 24,
            textAlign: "center",
            color: COLORS.muted,
            fontSize: 13,
          }}
        >
          No local LLM endpoints registered yet. Click <strong>Add Endpoint</strong> to
          connect your first Ollama / vLLM / LM Studio server.
        </div>
      )}

      {/* the list adapts to its own width: table → compact table → cards (localLlmLayout) */}
      <div ref={listRef}>
        {rows.length > 0 && (
          <div data-testid="local-llm-list" data-layout={layout}
            style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, overflowX: "auto" }}>
            {layout === "cards" ? (
              <div role="list" style={{ display: "grid" }}>
                {rows.map((r) => <EndpointRow key={r.id} row={r} layout={layout} {...rowHandlers} />)}
              </div>
            ) : (
              <table data-testid="local-llm-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <EndpointTableHead layout={layout} />
                <tbody>
                  {rows.map((r) => <EndpointRow key={r.id} row={r} layout={layout} {...rowHandlers} />)}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {modal === "create" && (
        <EndpointWizard
          providerClasses={providerClasses}
          onSaved={async () => { setModal(null); await reload(); }}
          onCancel={() => setModal(null)}
          onSeedRankings={onSeedRankings}
        />
      )}
      {modal === "edit" && editRow && (
        <EndpointEditModal
          row={editRow}
          providerClasses={providerClasses}
          onSaved={async () => { setModal(null); setEditRow(null); await reload(); }}
          onCancel={() => { setModal(null); setEditRow(null); }}
        />
      )}
    </div>
  );
}


// ─── Add Endpoint Wizard (F2 + F4 + F9) ────────────────────────────────


function EndpointWizard({ providerClasses, onSaved, onCancel, onSeedRankings }) {
  // Multi-step wizard: 1) details, 2) test, 3) starter template offer
  const [step, setStep] = useState(1);
  const [endpointId, setEndpointId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [providerClass, setProviderClass] = useState("local_openai_compat");
  const [baseUrl, setBaseUrl] = useState("http://host.docker.internal:11434/v1");
  const [apiKey, setApiKey] = useState("");
  const [defaultModel, setDefaultModel] = useState("");
  const [timeoutSeconds, setTimeoutSeconds] = useState(120);
  const [verifySsl, setVerifySsl] = useState(true);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createdSlug, setCreatedSlug] = useState(null);

  const slugValid = isValidSlug(endpointId);

  const runTest = useCallback(async () => {
    setTesting(true); setError(null); setTestResult(null);
    try {
      const res = await localLlmApi.testConnection({
        provider_class: providerClass,
        base_url: baseUrl,
        api_key: apiKey || null,
        default_model: defaultModel || null,
        timeout_seconds: Number(timeoutSeconds) || 120,
        verify_ssl: verifySsl,
      });
      setTestResult(res?.data || null);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setTesting(false);
    }
  }, [providerClass, baseUrl, apiKey, defaultModel, timeoutSeconds, verifySsl]);

  const handleSave = useCallback(async () => {
    setSaving(true); setError(null);
    try {
      const res = await localLlmApi.create({
        endpoint_id: endpointId,
        display_name: displayName || endpointId,
        provider_class: providerClass,
        base_url: baseUrl,
        api_key: apiKey || null,
        default_model: defaultModel || null,
        timeout_seconds: Number(timeoutSeconds) || 120,
        verify_ssl: verifySsl,
      });
      setCreatedSlug(res?.data?.endpoint_id || endpointId);
      setStep(3); // proceed to starter template offer (F9)
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  }, [
    endpointId, displayName, providerClass, baseUrl,
    apiKey, defaultModel, timeoutSeconds, verifySsl,
  ]);

  return (
    <Modal
      open
      onClose={onCancel}
      title="Add Local LLM Endpoint"
      width={620}
      footer={
        step === 1 ? (
          <>
            <Button variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!slugValid || !displayName || !baseUrl}
              onClick={() => setStep(2)}
            >
              Continue →
            </Button>
          </>
        ) : step === 2 ? (
          <>
            <Button variant="ghost" onClick={() => setStep(1)}>← Back</Button>
            <Button variant="secondary" onClick={runTest} disabled={testing}>
              {testing ? "Testing…" : "Test Connection"}
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save Endpoint"}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={onSaved}>Done</Button>
        )
      }
    >
      {step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Label>Slug (tenant-scoped, lowercase + dashes/underscores, max 63)</Label>
          <Input
            placeholder="ollama-laptop"
            value={endpointId}
            onChange={(e) => setEndpointId(e.target.value)}
          />
          {!slugValid && endpointId && (
            <div style={{ fontSize: 12, color: "#EF4444" }}>
              Slug must start with [a-z0-9] and contain only lowercase letters,
              digits, dashes, underscores. Reserved words (openai, default,
              admin, system…) are not allowed.
            </div>
          )}

          <Label>Display name</Label>
          <Input
            placeholder="Ollama on My Laptop"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />

          <Label>Provider class</Label>
          <Select
            value={providerClass}
            onChange={(e) => setProviderClass(e.target.value)}
          >
            {providerClasses.map(c => (
              <option key={c.key} value={c.key}>{c.display_name}</option>
            ))}
          </Select>
          <div style={{ fontSize: 11, color: COLORS.muted }}>
            {providerClasses.find(c => c.key === providerClass)?.description}
          </div>
        </div>
      )}

      {step === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Label>Base URL</Label>
          <Input
            placeholder="http://host.docker.internal:11434/v1"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
          />

          <Label>API Key (optional — Ollama doesn't require one)</Label>
          <Input
            type="password"
            placeholder="(leave blank for default Ollama)"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />

          <Label>Default Model (optional)</Label>
          <Input
            placeholder="llama-4-scout"
            value={defaultModel}
            onChange={(e) => setDefaultModel(e.target.value)}
          />

          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <Label>Timeout (seconds)</Label>
              <Input
                type="number"
                min={10}
                max={600}
                value={timeoutSeconds}
                onChange={(e) => setTimeoutSeconds(e.target.value)}
              />
            </div>
            <div>
              <Label>Verify SSL</Label>
              <TogglePill
                active={verifySsl}
                onClick={() => setVerifySsl(v => !v)}
              >
                {verifySsl ? "On" : "Off"}
              </TogglePill>
            </div>
          </div>

          {testResult && (
            <div
              style={{
                background: testResult.ok ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
                border: `1px solid ${testResult.ok ? "#10B981" : "#EF4444"}`,
                borderRadius: 8,
                padding: 12,
                fontSize: 12,
              }}
            >
              {testResult.ok ? (
                <>
                  ✓ Connection OK — {testResult.models_count} model(s) in {testResult.latency_ms}ms
                  {testResult.models_sample?.length > 0 && (
                    <div style={{ marginTop: 6, fontFamily: "monospace", color: COLORS.muted }}>
                      Sample: {testResult.models_sample.join(", ")}
                    </div>
                  )}
                </>
              ) : (
                <>✗ {testResult.error || "Unreachable"}</>
              )}
            </div>
          )}

          {error && (
            <div style={{ color: "#EF4444", fontSize: 12 }}>{error}</div>
          )}
        </div>
      )}

      {step === 3 && (
        <StarterTemplatesStep
          createdSlug={createdSlug}
          defaultModel={defaultModel}
          onSeed={onSeedRankings}
          onDone={onSaved}
        />
      )}
    </Modal>
  );
}


// ─── F9: starter ranking templates offered after first save ─────────────


function StarterTemplatesStep({ createdSlug, defaultModel, onSeed, onDone }) {
  const [seeding, setSeeding] = useState(null);

  const seed = useCallback(async (template) => {
    setSeeding(template);
    try {
      const chatEntries = buildStarterChatEntries(template, {
        endpointId: createdSlug,
        defaultModel,
      });
      // Flip allow_cloud_fallback first if Template B (so save-time
      // defense in PUT /llm-rankings doesn't reject the cloud entry).
      // Template A explicitly reaffirms the fail-closed default.
      await authApi.setAllowCloudFallback(template === "local_with_cloud_fallback");
      await authApi.updateLlmRankings("chat", chatEntries);
      if (onSeed) onSeed(template);
      onDone();
    } catch (err) {
      alert(`Failed to seed ranking: ${err.message || err}`);
    } finally {
      setSeeding(null);
    }
  }, [createdSlug, defaultModel, onSeed, onDone]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: 14, color: COLORS.text }}>
        ✓ Endpoint <code>{createdSlug}</code> saved. Pick a starter ranking to
        wire it into your chat pipeline:
      </div>

      <div
        style={{
          border: `1px solid ${COLORS.border}`,
          borderRadius: 10,
          padding: 16,
          background: COLORS.bgElevated,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
          🔒 Local Only (self-hosted) — default
        </div>
        <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 12 }}>
          Chat routes only to this endpoint. If it's unreachable, requests fail
          with a clear error — no quiet fallback to a cloud provider. Best for
          self-hosted and sovereign deployments.
        </div>
        <Button
          variant="primary"
          onClick={() => seed("local_only")}
          disabled={seeding !== null}
        >
          {seeding === "local_only" ? "Seeding…" : "Use Local Only"}
        </Button>
      </div>

      <div
        style={{
          border: `1px solid ${COLORS.border}`,
          borderRadius: 10,
          padding: 16,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
          ☁️ Local with Cloud Fallback
        </div>
        <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 12 }}>
          chat purpose tries this endpoint first, falls back to OpenAI
          (gpt-4o) on transient failure. Enables the Cloud LLM Fallback
          tenant toggle automatically.
        </div>
        <Button
          variant="secondary"
          onClick={() => seed("local_with_cloud_fallback")}
          disabled={seeding !== null}
        >
          {seeding === "local_with_cloud_fallback" ? "Seeding…" : "Use Local + Cloud Fallback"}
        </Button>
      </div>

      <Button variant="ghost" onClick={onDone}>
        Skip — I'll configure rankings manually
      </Button>
    </div>
  );
}


// ─── Edit modal (F3 sub-component) ─────────────────────────────────────


function EndpointEditModal({ row, providerClasses, onSaved, onCancel }) {
  const [displayName, setDisplayName] = useState(row.display_name || "");
  const [baseUrl, setBaseUrl] = useState(row.base_url);
  const [rotateKey, setRotateKey] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [defaultModel, setDefaultModel] = useState(row.default_model || "");
  const [timeoutSeconds, setTimeoutSeconds] = useState(row.timeout_seconds);
  const [verifySsl, setVerifySsl] = useState(row.verify_ssl);
  const [isOnDemand, setIsOnDemand] = useState(!!row.is_on_demand);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = useCallback(async () => {
    setSaving(true); setError(null);
    try {
      const payload = {
        display_name: displayName,
        base_url: baseUrl,
        default_model: defaultModel || null,
        timeout_seconds: Number(timeoutSeconds) || 120,
        verify_ssl: verifySsl,
        is_on_demand: isOnDemand,
      };
      if (rotateKey) payload.api_key = apiKey; // "" explicitly clears
      await localLlmApi.update(row.endpoint_id, payload);
      onSaved();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  }, [
    row.endpoint_id, displayName, baseUrl, rotateKey, apiKey,
    defaultModel, timeoutSeconds, verifySsl, isOnDemand, onSaved,
  ]);

  return (
    <Modal
      open
      onClose={onCancel}
      title={`Edit Endpoint — ${row.endpoint_id}`}
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Label>Display name</Label>
        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />

        <Label>Base URL</Label>
        <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />

        <div>
          <Label>API Key</Label>
          <div
            style={{
              padding: "8px 12px",
              background: COLORS.bgElevated,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              fontSize: 12,
              fontFamily: "monospace",
              color: COLORS.muted,
              marginBottom: 8,
            }}
          >
            {row.api_key_set ? "•••• (key stored — never returned)" : "(no key configured)"}
          </div>
          <TogglePill
            active={rotateKey}
            onClick={() => setRotateKey(v => !v)}
          >
            Rotate key
          </TogglePill>
          {rotateKey && (
            <Input
              type="password"
              placeholder="New API key (leave blank to clear)"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              style={{ marginTop: 8 }}
            />
          )}
        </div>

        <Label>Default Model</Label>
        <Input value={defaultModel} onChange={(e) => setDefaultModel(e.target.value)} />

        <div style={{ display: "flex", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <Label>Timeout (seconds)</Label>
            <Input
              type="number"
              value={timeoutSeconds}
              onChange={(e) => setTimeoutSeconds(e.target.value)}
            />
          </div>
          <div>
            <Label>Verify SSL</Label>
            <TogglePill active={verifySsl} onClick={() => setVerifySsl(v => !v)}>
              {verifySsl ? "On" : "Off"}
            </TogglePill>
          </div>
        </div>

        <div>
          <Label>On-demand (scale-to-zero GPU)</Label>
          <TogglePill active={isOnDemand} onClick={() => setIsOnDemand(v => !v)}>
            {isOnDemand ? "On — warms on use, parks when idle" : "Off — always-on host"}
          </TogglePill>
          <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 6, lineHeight: 1.5 }}>
            On = this endpoint's model runs on the on-demand GPU cluster: it scales
            to zero when idle (no cost) and warms up on the next request, showing a
            Red/Amber/Green status light. Off = a standard always-on server (Ollama, LM Studio).
          </div>
        </div>

        {error && <div style={{ color: "#EF4444", fontSize: 12 }}>{error}</div>}
      </div>
    </Modal>
  );
}

